/**
 * Usage Example
 *
 * <button
 *   type="button"
 *   data-modal-target="#exampleModal"
 *   data-modal-action="open"
 * >
 *   Open Modal
 * </button>
 *
 * <sdg-modal
 *   id="exampleModal"
 *   aria-labelledby="exampleModalTitle"
 * >
 *   <div class="modal__overlay" data-modal-overlay></div>
 *
 *   <div class="modal__container">
 *     <header class="modal__header">
 *       <h2
 *         id="exampleModalTitle"
 *         class="modal__title"
 *       >
 *         Modal Title
 *       </h2>
 *
 *       <button
 *         type="button"
 *         class="modal__close"
 *         data-modal-close
 *         aria-label="Close"
 *       >
 *         ✕
 *       </button>
 *     </header>
 *
 *     <div class="modal__content">
 *       <!-- Modal content goes here -->
 *     </div>
 *   </div>
 * </sdg-modal>
 *
 * Notes:
 * - data-modal-target must match the modal's id.
 * - data-modal-action supports: "open" (default), "close", "toggle".
 */

const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusables(root) {
  if (!root) return [];
  return Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => {
    if (!(el instanceof HTMLElement)) return false;
    if (el.closest('[inert]')) return false;
    if (el.closest('fieldset[disabled]')) return false;
    const style = window.getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden' && el.getClientRects().length > 0;
  });
}

let scrollLocks = 0;
let prevOverflow = '';

function lockScroll() {
  scrollLocks += 1;
  if (scrollLocks !== 1) return;

  const root = document.documentElement;
  prevOverflow = root.style.overflow || '';
  root.style.overflow = 'hidden';
  root.classList.add('modal-open');
}

function unlockScroll() {
  scrollLocks = Math.max(0, scrollLocks - 1);
  if (scrollLocks !== 0) return;

  const root = document.documentElement;
  root.style.overflow = prevOverflow;
  root.classList.remove('modal-open');
}

class Modal extends HTMLElement {
  static get observedAttributes() {
    return ['open'];
  }

  constructor() {
    super();
    this._container = null;
    this._prevActive = null;
    this._suppressAttr = false;
    this._teleportOrigin = null;
    this._teleporting = false;

    this._onClick = this._onClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onFocusIn = this._onFocusIn.bind(this);
  }

  /**
   * Connected callback for the modal
   */
  connectedCallback() {
    if (this._teleporting) return;
    this._container = this.querySelector('.modal__container');

    if (this._container) {
      this._container.setAttribute('role', 'dialog');
      this._container.setAttribute('aria-modal', 'true');
      if (!this._container.hasAttribute('tabindex')) {
        this._container.setAttribute('tabindex', '-1');
      }

      const labelledBy = this.getAttribute('aria-labelledby');
      if (labelledBy) {
        this._container.setAttribute('aria-labelledby', labelledBy);
      }
    }

    this.setAttribute('aria-hidden', this.isOpen ? 'false' : 'true');
    this.addEventListener('click', this._onClick);

    if (this.isOpen) this._applyOpen(true, { restoreFocus: false });
  }

  /**
   * Disconnect the modal
   */
  disconnectedCallback() {
    if (this._teleporting) return;
    this.removeEventListener('click', this._onClick);
    if (this.isOpen) this._applyOpen(false);
  }

  /**
   * Handle attribute changes for the modal
   * @param {string} name - The name of the attribute
   * @param {string} oldVal - The old value of the attribute
   * @param {string} newVal - The new value of the attribute
   */
  attributeChangedCallback(name, oldVal, newVal) {
    if (name !== 'open' || oldVal === newVal) return;
    if (!this.isConnected) return;
    if (this._suppressAttr) return;
    this._applyOpen(this.isOpen);
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  /**
   * Open the modal
   * @param {Object} options - The options
   * @param {HTMLElement} [options.opener] - The opener element
   */
  open({ opener } = {}) {
    if (this.isOpen) return;

    this._prevActive =
      opener instanceof HTMLElement
        ? opener
        : document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;

    this.setAttribute('open', '');
  }

  /**
   * Close the modal
   */
  close({ restoreFocus = true } = {}) {
    if (!this.isOpen) return;
    if (!restoreFocus) this._prevActive = null;
    this.removeAttribute('open');
  }

  /**
   * Toggle the modal
   * @param {Object} options - The options
   * @param {HTMLElement} [options.opener] - The opener element
   */
  toggle({ opener } = {}) {
    this.isOpen ? this.close() : this.open({ opener });
  }

  /**
   * Teleport the modal to the home element
   */
  _teleport() {
    if (!this.hasAttribute('data-teleport-home')) return;

    const root = document.getElementById('rootModal');
    if (!root || root.contains(this)) return;

    this._teleportOrigin = {
      parent: this.parentNode,
      nextSibling: this.nextSibling,
    };

    try {
      this._teleporting = true;
      root.appendChild(this);
    } catch {
      this._teleportOrigin = null;
    } finally {
      this._teleporting = false;
    }
  }

  /**
   * Restore the modal to the original position
   */
  _restore() {
    if (!this.hasAttribute('data-teleport-home')) return;

    const origin = this._teleportOrigin;
    if (!origin) return;

    this._teleportOrigin = null;

    try {
      this._teleporting = true;
      origin.parent?.insertBefore(this, origin.nextSibling);
    } catch {
      // silently fail
    } finally {
      this._teleporting = false;
    }
  }

  /**
   * Handle click events for the modal
   * @param {MouseEvent} e - The click event
   */
  _onClick(e) {
    const target = e.target;
    if (!(target instanceof Element)) return;

    if (target.matches('[data-modal-overlay]')) {
      e.preventDefault();
      this.close();
      return;
    }

    if (target.closest('[data-modal-close]')) {
      e.preventDefault();
      this.close();
    }
  }

  /**
   * Handle keydown events for the modal
   * @param {KeyboardEvent} e - The keydown event
   */
  _onKeydown(e) {
    if (!this.isOpen) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
      return;
    }

    if (e.key !== 'Tab') return;

    const focusables = getFocusables(this._container);

    if (!focusables.length) {
      e.preventDefault();
      this._container?.focus();
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (e.shiftKey) {
      if (active === first || active === this._container) {
        e.preventDefault();
        last.focus();
      }
      return;
    }

    if (active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  _onFocusIn(e) {
    if (!this.isOpen || !this._container) return;

    const target = e.target;
    if (!(target instanceof Node)) return;

    if (!this._container.contains(target)) {
      const focusables = getFocusables(this._container);
      (focusables[0] || this._container).focus();
    }
  }

  /**
   * Apply the open state to the modal
   * @param {boolean} isOpen - The open state
   * @param {Object} options - The options
   * @param {boolean} [options.restoreFocus=true] - Whether to restore focus
   */
  _applyOpen(isOpen, { restoreFocus = true } = {}) {
    this.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    if (!this._container) return;

    if (isOpen) {
      document.querySelectorAll('sdg-modal[open]').forEach((m) => {
        if (m === this) return;
        if (!(m instanceof Modal)) return;

        m._suppressAttr = true;
        m._applyOpen(false, { restoreFocus: false });
        m.removeAttribute('open');
        m._suppressAttr = false;
      });

      this._teleport();
      lockScroll();
      document.addEventListener('keydown', this._onKeydown, true);
      document.addEventListener('focusin', this._onFocusIn, true);

      this._container.focus();
      return;
    }

    document.removeEventListener('keydown', this._onKeydown, true);
    document.removeEventListener('focusin', this._onFocusIn, true);
    unlockScroll();
    this._restore();

    if (restoreFocus) {
      const prev = this._prevActive;
      this._prevActive = null;
      prev?.focus();
    } else {
      this._prevActive = null;
    }
  }
}

/**
 * Resolve the modal element from the selector
 * @param {string} selector - The selector to resolve the modal element from
 * @returns {Modal | null} The modal element or null if not found
 */
function resolveModal(selector) {
  if (!selector) return null;
  try {
    const el = document.querySelector(selector);
    return el?.tagName === 'SDG-MODAL' ? el : null;
  } catch {
    console.warn(`[sdg-modal] Invalid selector: "${selector}"`);
    return null;
  }
}

/**
 * Handle document click events
 * @param {MouseEvent} e - The click event
 */
function onDocumentClick(e) {
  const trigger =
    e.target instanceof Element ? e.target.closest('[data-modal-target]') : null;

  if (!trigger) return;

  const selector = trigger.getAttribute('data-modal-target');
  const action = (trigger.getAttribute('data-modal-action') || 'open').toLowerCase();
  const modal = resolveModal(selector);

  if (!modal) return;

  e.preventDefault();

  if (action === 'close') {
    modal.close();
    return;
  }

  if (action === 'toggle') {
    modal.toggle({ opener: trigger });
    return;
  }

  modal.open({ opener: trigger });
}

/**
 * Initialize the SDG Modal
 */
(function initSDGModal() {
  if (!customElements.get('sdg-modal')) {
    customElements.define('sdg-modal', Modal);
  }

  document.removeEventListener('click', onDocumentClick);
  document.addEventListener('click', onDocumentClick);
})();
